import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Clock3, Database, Download, FileArchive, Loader2, Play, RefreshCcw, ShieldAlert, Upload } from 'lucide-react';

import { dataMigrationAPI, getErrorMessage } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLanguage } from '@/i18n';
import { formatDateTime } from '@/lib/utils';

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const toggleValue = (values, nextValue) => (
  values.includes(nextValue)
    ? values.filter((value) => value !== nextValue)
    : [...values, nextValue]
);

const LEGACY_DATABASE_NAMES = new Set(['test_database', 'cgbusiness_erp', 'cg_business', 'cgbusiness', 'erp_db']);
const sanitizeDatabaseName = (value, fallback = 'CG_DataBase') => {
  const normalizedValue = (value || '').trim();
  if (!normalizedValue) return fallback;
  return LEGACY_DATABASE_NAMES.has(normalizedValue) ? fallback : normalizedValue;
};

const LIVE_PRODUCTION_PROMOTION_PRESETS = [
  {
    key: 'expense-catalog',
    title: 'PGT live → produção',
    description: 'Promove categorias e subcategorias PGT criadas/ajustadas no live para a produção.',
    collections: ['expense_categories'],
  },
  {
    key: 'suppliers',
    title: 'Fornecedores live → produção',
    description: 'Leva fornecedores novos/editados do live para a produção de forma controlada.',
    collections: ['suppliers'],
  },
  {
    key: 'operations-pack',
    title: 'Pacote operacional live → produção',
    description: 'Combina o catálogo PGT e fornecedores no mesmo pacote de promoção.',
    collections: ['expense_categories', 'suppliers'],
  },
];

export const DataMigrationTab = ({ canImport, canExport }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historySearch, setHistorySearch] = useState('');
  const [historyOperationFilter, setHistoryOperationFilter] = useState('all');
  const [targetDb, setTargetDb] = useState('');
  const [sourceDb, setSourceDb] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [clearCollections, setClearCollections] = useState([]);
  const [busyAction, setBusyAction] = useState('');
  const [lastImportResult, setLastImportResult] = useState(null);

  const loadSummary = async () => {
    try {
      setLoading(true);
      const response = await dataMigrationAPI.getSummary();
      const payload = response.data || {};
      setSummary(payload);
      const configuredDb = sanitizeDatabaseName(payload?.configured_target_db, 'CG_DataBase');
      const primaryDb = sanitizeDatabaseName(
        payload?.configured_target_db || payload?.manifest?.primary_source_db || payload?.export_summary?.principal_database?.name,
        configuredDb,
      );
      const activeDb = sanitizeDatabaseName(configuredDb || primaryDb, 'CG_DataBase');
      setSourceDb(activeDb);
      setTargetDb(activeDb);
    } catch (error) {
      toast.error(getErrorMessage(error, t('migration.summaryError')));
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await dataMigrationAPI.getHistory(15);
      setHistoryEntries(response.data?.items || []);
    } catch (error) {
      toast.error(getErrorMessage(error, t('migration.historyError')));
    }
  };

  useEffect(() => {
    loadSummary();
    loadHistory();
  }, []);

  const recommendedCollections = useMemo(() => (
    summary?.manifest?.recommended_order?.map((item) => item.collection) || []
  ), [summary]);

  const activeDatabaseName = sanitizeDatabaseName(summary?.configured_target_db || targetDb || sourceDb, 'CG_DataBase');
  const exportedCollectionsCount = useMemo(() => {
    const exportedDbs = summary?.export_summary?.exported_databases || {};
    return Object.values(exportedDbs).reduce((total, dbInfo) => total + (dbInfo?.total_collection_count || 0), 0);
  }, [summary]);

  const filteredHistoryEntries = useMemo(() => {
    const normalizedSearch = historySearch.trim().toLowerCase();

    return historyEntries.filter((entry) => {
      const matchesOperation = historyOperationFilter === 'all' || entry.operation_type === historyOperationFilter;
      if (!matchesOperation) return false;
      if (!normalizedSearch) return true;

      const haystack = [
        entry.created_by_name,
        entry.source_db,
        entry.target_db,
        ...(entry.file_names || []),
      ].filter(Boolean).join(' ').toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [historyEntries, historyOperationFilter, historySearch]);

  const buildFormData = () => {
    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append('files', file));
    formData.append('target_db', sanitizeDatabaseName(targetDb.trim(), activeDatabaseName));
    formData.append('source_db', sanitizeDatabaseName(sourceDb, activeDatabaseName));
    formData.append('selected_collections', JSON.stringify(selectedCollections));
    formData.append('clear_collections', JSON.stringify(clearCollections));
    return formData;
  };

  const handleExport = async () => {
    try {
      setBusyAction('export');
      const response = await dataMigrationAPI.downloadExport('primary');
      downloadBlob(response.data, 'erp-active-database-package.zip');
      toast.success(t('migration.exportSuccess'));
      await loadSummary();
      await loadHistory();
    } catch (error) {
      toast.error(getErrorMessage(error, t('migration.exportError')));
    } finally {
      setBusyAction('');
    }
  };

  const handleImportAction = async (mode) => {
    if (!selectedFiles.length) {
      toast.error('Selecione um ficheiro ZIP ou JSON para importar');
      return;
    }
    if (!targetDb.trim()) {
      toast.error('Indique a base de destino');
      return;
    }

    try {
      setBusyAction(mode);
      const formData = buildFormData();
      const response = mode === 'dry-run'
        ? await dataMigrationAPI.dryRunImport(formData)
        : await dataMigrationAPI.runImport(formData);
      setLastImportResult(response.data || null);
      toast.success(mode === 'dry-run' ? t('migration.dryRunSuccess') : t('migration.importSuccess'));
      await loadHistory();
    } catch (error) {
      toast.error(getErrorMessage(error, mode === 'dry-run' ? t('migration.dryRunError') : t('migration.importError')));
    } finally {
      setBusyAction('');
    }
  };

  const handleApplyPromotionPreset = (preset) => {
    setSelectedCollections(preset.collections);
    setClearCollections([]);
    toast.success(`Preset aplicado: ${preset.title}`);
  };

  const handleDownloadHistoryReport = async (entryId) => {
    try {
      const response = await dataMigrationAPI.downloadHistoryReport(entryId);
      downloadBlob(response.data, `migration-report-${entryId}.json`);
      toast.success(t('migration.reportSuccess'));
    } catch (error) {
      toast.error(getErrorMessage(error, t('migration.reportError')));
    }
  };

  const getOperationLabel = (operationType) => {
    if (operationType === 'export') return t('migration.exportType');
    if (operationType === 'import') return t('migration.importType');
    return t('migration.dryRunType');
  };

  const importTotals = lastImportResult?.import_report?.totals;
  const validationTotals = lastImportResult?.validation_report?.totals;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="data-migration-loading-state">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-migration-tab-content">
      <Card data-testid="data-migration-overview-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Database className="h-5 w-5" /> {t('migration.title')}</CardTitle>
          <CardDescription>
            {t('migration.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border p-4" data-testid="data-migration-primary-db-card">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('migration.activeDatabase')}</p>
            <p className="mt-2 text-lg font-semibold">{activeDatabaseName}</p>
            <p className="mt-2 text-xs text-muted-foreground">{t('migration.activeDatabaseDescription')}</p>
          </div>
          <div className="rounded-2xl border p-4" data-testid="data-migration-related-dbs-card">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('migration.architectureLabel')}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge variant="outline" data-testid="data-migration-single-db-badge">{t('migration.singleDatabaseBadge')}</Badge>
              <Badge variant="outline" data-testid="data-migration-collections-count-badge">{t('migration.collectionsCount', { count: exportedCollectionsCount })}</Badge>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">{t('migration.singleDatabaseDescription')}</p>
          </div>
          <div className="rounded-2xl border p-4" data-testid="data-migration-permissions-card">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('migration.activePermissions')}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge variant={canExport ? 'default' : 'outline'} data-testid="data-migration-export-permission-badge">{t('migration.permissionsExport')}</Badge>
              <Badge variant={canImport ? 'default' : 'outline'} data-testid="data-migration-import-permission-badge">{t('migration.permissionsImport')}</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="data-migration-export-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Download className="h-5 w-5" /> {t('migration.exportCardTitle')}</CardTitle>
          <CardDescription>{t('migration.exportCardDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-4 rounded-2xl border p-4 md:flex-row md:items-end md:justify-between" data-testid="data-migration-export-single-db-card">
            <div className="space-y-2">
              <Label>{t('migration.exportContextLabel')}</Label>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" data-testid="data-migration-export-context-badge">{activeDatabaseName}</Badge>
                <Badge variant="outline" data-testid="data-migration-export-architecture-badge">{t('migration.singleDatabaseBadge')}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">{t('migration.exportContextDescription')}</p>
            </div>
            <div className="flex items-end">
              <Button onClick={handleExport} disabled={!canExport || busyAction === 'export'} data-testid="data-migration-export-button">
                {busyAction === 'export' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileArchive className="mr-2 h-4 w-4" />}
                {t('migration.generateExport')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="data-migration-live-production-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><RefreshCcw className="h-5 w-5" /> Promoção segura live → produção</CardTitle>
          <CardDescription>
            Use este fluxo para promover alterações do live para a produção com controlo manual. Evita sync bidireccional automático e reduz risco de conflito de dados.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-950" data-testid="data-migration-live-production-warning">
            <p className="text-sm font-medium">Recomendação operacional</p>
            <p className="mt-1 text-xs text-amber-900/80">
              Fonte principal escolhida: <strong>Live → Produção</strong>. Gere o pacote no live, seleccione apenas as coleções necessárias e importe depois na produção com revisão controlada.
            </p>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            {LIVE_PRODUCTION_PROMOTION_PRESETS.map((preset) => (
              <div key={preset.key} className="rounded-2xl border p-4" data-testid={`data-migration-preset-card-${preset.key}`}>
                <p className="text-sm font-semibold text-foreground">{preset.title}</p>
                <p className="mt-2 text-xs text-muted-foreground">{preset.description}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {preset.collections.map((collectionName) => (
                    <Badge key={`${preset.key}-${collectionName}`} variant="outline" data-testid={`data-migration-preset-collection-${preset.key}-${collectionName}`}>
                      {collectionName}
                    </Badge>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="mt-4 w-full"
                  onClick={() => handleApplyPromotionPreset(preset)}
                  data-testid={`data-migration-preset-button-${preset.key}`}
                >
                  Aplicar preset
                </Button>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="data-migration-live-production-steps">
            <p className="text-sm font-semibold text-slate-900">Passos sugeridos</p>
            <ol className="mt-3 space-y-2 text-xs text-muted-foreground">
              <li>1. No live, gere o pacote da base activa em <strong>Exportar</strong>.</li>
              <li>2. Aplique um preset de promoção para pré-seleccionar as coleções críticas.</li>
              <li>3. Na produção, faça primeiro um <strong>Dry Run</strong> e depois a importação real.</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="data-migration-import-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Upload className="h-5 w-5" /> {t('migration.importCardTitle')}</CardTitle>
          <CardDescription>
            {t('migration.importCardDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2 rounded-2xl border p-4" data-testid="data-migration-import-target-card">
              <Label>{t('migration.activeDatabase')}</Label>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" data-testid="data-migration-target-db-input">{activeDatabaseName}</Badge>
                <Badge variant="outline" data-testid="data-migration-source-db-select">{t('migration.singleDatabaseBadge')}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">{t('migration.importContextDescription')}</p>
            </div>
            <div className="space-y-2 rounded-2xl border p-4" data-testid="data-migration-import-context-card">
              <Label>{t('migration.importContextTitle')}</Label>
              <p className="text-sm text-foreground">{t('migration.importContextPrimary')}</p>
              <p className="text-xs text-muted-foreground">{t('migration.importContextSecondary')}</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="migration-files">{t('migration.files')}</Label>
            <Input id="migration-files" type="file" multiple accept=".zip,.json" onChange={(event) => setSelectedFiles(Array.from(event.target.files || []))} data-testid="data-migration-file-input" />
            <p className="text-xs text-muted-foreground" data-testid="data-migration-selected-files-count">{t('migration.filesSelected', { count: selectedFiles.length })}</p>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-3 rounded-2xl border p-4" data-testid="data-migration-collections-selector">
              <div>
                <p className="font-medium">{t('migration.collectionsToImport')}</p>
                <p className="text-xs text-muted-foreground">{t('migration.collectionsToImportHelp')}</p>
              </div>
              <ScrollArea className="h-52 rounded-xl border p-3">
                <div className="space-y-3">
                  {recommendedCollections.map((collectionName) => (
                    <label key={collectionName} className="flex items-center gap-3" data-testid={`data-migration-collection-option-${collectionName}`}>
                      <Checkbox checked={selectedCollections.includes(collectionName)} onCheckedChange={() => setSelectedCollections((current) => toggleValue(current, collectionName))} data-testid={`data-migration-collection-checkbox-${collectionName}`} />
                      <span className="text-sm">{collectionName}</span>
                    </label>
                  ))}
                </div>
              </ScrollArea>
            </div>

            <div className="space-y-3 rounded-2xl border p-4" data-testid="data-migration-clear-selector">
              <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-900">
                <ShieldAlert className="mt-0.5 h-4 w-4" />
                <p className="text-xs">{t('migration.clearWarning')}</p>
              </div>
              <ScrollArea className="h-52 rounded-xl border p-3">
                <div className="space-y-3">
                  {recommendedCollections.map((collectionName) => (
                    <label key={`clear-${collectionName}`} className="flex items-center gap-3" data-testid={`data-migration-clear-option-${collectionName}`}>
                      <Checkbox checked={clearCollections.includes(collectionName)} onCheckedChange={() => setClearCollections((current) => toggleValue(current, collectionName))} data-testid={`data-migration-clear-checkbox-${collectionName}`} />
                      <span className="text-sm">{collectionName}</span>
                    </label>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => handleImportAction('dry-run')} disabled={!canImport || busyAction === 'dry-run'} data-testid="data-migration-dry-run-button">
              {busyAction === 'dry-run' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              {t('migration.dryRun')}
            </Button>
            <Button onClick={() => handleImportAction('run')} disabled={!canImport || busyAction === 'run'} data-testid="data-migration-run-import-button">
              {busyAction === 'run' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              {t('migration.importNow')}
            </Button>
            <Button variant="ghost" onClick={loadSummary} disabled={busyAction !== ''} data-testid="data-migration-refresh-summary-button">
              <RefreshCcw className="mr-2 h-4 w-4" />
              {t('migration.refreshSummary')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {lastImportResult && (
        <Card data-testid="data-migration-result-card">
          <CardHeader>
            <CardTitle>{t('migration.latestOperation')}</CardTitle>
            <CardDescription>{t('migration.latestOperationDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-5">
              <Badge variant="outline" data-testid="data-migration-result-read">{t('migration.read')}: {importTotals?.read || 0}</Badge>
              <Badge variant="outline" data-testid="data-migration-result-inserted">{t('migration.inserted')}: {importTotals?.inserted || 0}</Badge>
              <Badge variant="outline" data-testid="data-migration-result-updated">{t('migration.updated')}: {importTotals?.updated || 0}</Badge>
              <Badge variant="outline" data-testid="data-migration-result-ignored">{t('migration.ignored')}: {importTotals?.ignored || 0}</Badge>
              <Badge variant="outline" data-testid="data-migration-result-errors">{t('migration.errors')}: {importTotals?.errors || 0}</Badge>
            </div>
            {validationTotals && (
              <div className="grid gap-3 md:grid-cols-4" data-testid="data-migration-validation-summary">
                <Badge variant="outline">{t('migration.validationVerifiable')}: {validationTotals.verifiable_docs || 0}</Badge>
                <Badge variant="outline">{t('migration.validationMatched')}: {validationTotals.matched_docs || 0}</Badge>
                <Badge variant="outline">{t('migration.validationMissing')}: {validationTotals.missing_docs || 0}</Badge>
                <Badge variant="outline">{t('migration.validationUnverifiable')}: {validationTotals.unverifiable_docs || 0}</Badge>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card data-testid="data-migration-history-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Clock3 className="h-5 w-5" /> {t('migration.historyTitle')}</CardTitle>
          <CardDescription>{t('migration.historyDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 lg:grid-cols-[1fr_220px_auto] lg:items-end">
            <div className="space-y-2">
              <Label htmlFor="migration-history-search">{t('labels.search')}</Label>
              <Input
                id="migration-history-search"
                value={historySearch}
                onChange={(event) => setHistorySearch(event.target.value)}
                placeholder={t('migration.searchPlaceholder')}
                data-testid="data-migration-history-search-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="migration-history-operation-filter">{t('labels.type')}</Label>
              <Select value={historyOperationFilter} onValueChange={setHistoryOperationFilter}>
                <SelectTrigger id="migration-history-operation-filter" data-testid="data-migration-history-operation-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('migration.filterAllOperations')}</SelectItem>
                  <SelectItem value="export">{t('migration.exportType')}</SelectItem>
                  <SelectItem value="import">{t('migration.importType')}</SelectItem>
                  <SelectItem value="dry-run">{t('migration.dryRunType')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button variant="ghost" onClick={loadHistory} data-testid="data-migration-history-refresh-button">
              <RefreshCcw className="mr-2 h-4 w-4" />
              {t('migration.refreshSummary')}
            </Button>
          </div>

          {historyEntries.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="data-migration-history-empty-state">{t('migration.noHistory')}</p>
          ) : filteredHistoryEntries.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="data-migration-history-no-filtered-results">{t('migration.noFilteredHistory')}</p>
          ) : (
            <div className="space-y-3">
              {filteredHistoryEntries.map((entry) => (
                <div key={entry.id} className="rounded-2xl border p-4" data-testid={`data-migration-history-item-${entry.id}`}>
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">{getOperationLabel(entry.operation_type)}</Badge>
                        <Badge variant={entry.status === 'success' ? 'default' : 'destructive'}>{entry.status}</Badge>
                        <span className="text-xs text-muted-foreground">{formatDateTime(entry.created_at)}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t('migration.performedBy')}: <span className="font-medium text-foreground">{entry.created_by_name || '—'}</span>
                      </p>
                      <div className="grid gap-2 text-sm md:grid-cols-2">
                        <p><span className="font-medium">{t('migration.sourceDbLabel')}:</span> {sanitizeDatabaseName(entry.source_db, activeDatabaseName) || '—'}</p>
                        <p><span className="font-medium">{t('migration.targetDbLabel')}:</span> {sanitizeDatabaseName(entry.target_db, activeDatabaseName) || '—'}</p>
                        <p><span className="font-medium">{t('migration.selectedCollections')}:</span> {(entry.selected_collections || []).length || 0}</p>
                        <p><span className="font-medium">{t('migration.clearCollections')}:</span> {(entry.clear_collections || []).length || 0}</p>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs">
                        <Badge variant="outline">{t('migration.read')}: {entry.totals?.read ?? entry.totals?.documents ?? 0}</Badge>
                        <Badge variant="outline">{t('migration.inserted')}: {entry.totals?.inserted ?? 0}</Badge>
                        <Badge variant="outline">{t('migration.updated')}: {entry.totals?.updated ?? 0}</Badge>
                        <Badge variant="outline">{t('migration.errors')}: {entry.totals?.errors ?? 0}</Badge>
                      </div>
                    </div>

                    <Button variant="outline" onClick={() => handleDownloadHistoryReport(entry.id)} data-testid={`data-migration-history-download-${entry.id}`}>
                      <Download className="mr-2 h-4 w-4" />
                      {t('migration.downloadReport')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};